<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';

import { api } from '../../api/client.js';

/**
 * Usuários do RFID (WA01): só quem já entrou ou foi pré-cadastrado; não há
 * diretório central. O servidor aplica tudo; esta tela só monta o estado
 * desejado completo de clientes e fazendas.
 */

type Status = 'pending' | 'preapproved' | 'active' | 'suspended' | 'cancelled';

interface UserItem {
  id: string;
  email: string;
  displayName: string | null;
  status: Status;
  linked: boolean;
  lastLoginAtMs: number | null;
  tenantCount?: number;
}

interface UserDetail extends UserItem {
  grants: { tenantId: string; tenantName: string; allFarms: boolean; farms: { farmId: string; name: string }[] }[];
}

interface Named {
  id: string;
  name: string;
}

const STATUS_LABEL: Record<Status, string> = {
  pending: 'Aguardando liberação',
  preapproved: 'Pré-cadastrado',
  active: 'Ativo',
  suspended: 'Suspenso',
  cancelled: 'Cancelado',
};

const users = ref<UserItem[]>([]);
const nextCursor = ref<string | null>(null);
const statusFilter = ref<Status | ''>('');
const search = ref('');
const newEmail = ref('');
const selected = ref<UserDetail | null>(null);
const tenants = ref<Named[]>([]);
const farmsByTenant = reactive<Record<string, Named[]>>({});
/** Estado desejado: cliente → toggle geral + fazendas individuais (preservadas com o toggle ligado). */
const draft = reactive(new Map<string, { allFarms: boolean; farmIds: Set<string> }>());
const message = ref<string | null>(null);
const busy = ref(false);

const dateFormat = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' });
const lastLogin = (ms: number | null) => (ms === null ? 'nunca entrou' : dateFormat.format(ms));

async function loadUsers(reset = true): Promise<void> {
  const result = await api.accessUsers<{ items: UserItem[]; nextCursor: string | null }>({
    status: statusFilter.value,
    search: search.value.trim(),
    cursor: reset ? undefined : (nextCursor.value ?? undefined),
  });
  if (result.status !== 200) {
    message.value = 'Não foi possível carregar os usuários.';
    return;
  }
  users.value = reset ? result.body.items : [...users.value, ...result.body.items];
  nextCursor.value = result.body.nextCursor;
}

async function loadTenants(): Promise<void> {
  const result = await api.list<{ items: Named[] }>('/admin/tenants?limit=100');
  tenants.value = result.body.items ?? [];
}

/** Fazendas de UM cliente, só quando o admin abre aquele cliente. */
async function loadFarms(tenantId: string): Promise<void> {
  if (farmsByTenant[tenantId] !== undefined) return;
  const result = await api.list<{ items: Named[] }>(`/admin/tenants/${encodeURIComponent(tenantId)}/farms?limit=100`);
  farmsByTenant[tenantId] = result.body.items ?? [];
}

async function open(id: string): Promise<void> {
  message.value = null;
  const result = await api.accessUser<UserDetail>(id);
  if (result.status !== 200) {
    message.value = 'Usuário não encontrado.';
    return;
  }
  show(result.body);
}

function show(detail: UserDetail): void {
  selected.value = detail;
  draft.clear();
  for (const grant of detail.grants) {
    draft.set(grant.tenantId, { allFarms: grant.allFarms, farmIds: new Set(grant.farms.map((farm) => farm.farmId)) });
    void loadFarms(grant.tenantId);
  }
}

function toggleTenant(tenantId: string, checked: boolean): void {
  if (checked) {
    draft.set(tenantId, { allFarms: true, farmIds: new Set() });
    void loadFarms(tenantId);
  } else {
    draft.delete(tenantId);
  }
}

/** Ligar "Todas as fazendas" só desabilita as individuais: a seleção fica guardada. */
function setAllFarms(tenantId: string, checked: boolean): void {
  const grant = draft.get(tenantId);
  if (grant !== undefined) grant.allFarms = checked;
}

function toggleFarm(tenantId: string, farmId: string, checked: boolean): void {
  const grant = draft.get(tenantId);
  if (grant === undefined) return;
  if (checked) grant.farmIds.add(farmId);
  else grant.farmIds.delete(farmId);
}

async function run(action: () => Promise<{ status: number; body: unknown }>, success: string): Promise<boolean> {
  busy.value = true;
  message.value = null;
  try {
    const result = await action();
    if (result.status >= 200 && result.status < 300) {
      message.value = success;
      const body = result.body as UserDetail;
      if (body.grants !== undefined) show(body);
      await loadUsers();
      return true;
    }
    const code = (result.body as { errorCode?: string }).errorCode;
    message.value =
      code === 'PRE_REGISTRATION_CONFLICT'
        ? 'Este email já está cadastrado de outra forma.'
        : code === 'ACCESS_STATUS_TRANSITION_INVALID'
          ? 'Esta mudança de situação não é permitida.'
          : code === 'EMAIL_INVALID'
            ? 'Email inválido.'
            : 'Não foi possível salvar.';
    return false;
  } finally {
    busy.value = false;
  }
}

async function preRegister(): Promise<void> {
  const email = newEmail.value.trim();
  if (email === '') return;
  if (await run(() => api.preRegister(email, []), 'Pré-cadastro salvo. Escolha os clientes e fazendas.')) {
    newEmail.value = '';
  }
}

function saveGrants(): Promise<boolean> {
  const user = selected.value!;
  const grants = [...draft.entries()].map(([tenantId, grant]) => ({
    tenantId,
    allFarms: grant.allFarms,
    farmIds: [...grant.farmIds],
  }));
  return run(() => api.setAccessGrants(user.id, grants), 'Acesso atualizado.');
}

function setStatus(status: Status): Promise<boolean> {
  return run(() => api.setAccessStatus(selected.value!.id, status), 'Situação atualizada.');
}

const actions = computed(() => {
  const user = selected.value;
  if (user === null) return [];
  const list: { label: string; status: Status }[] = [];
  if (user.status === 'active') list.push({ label: 'Suspender', status: 'suspended' });
  if (user.status === 'pending') list.push({ label: 'Liberar acesso', status: 'active' });
  if (user.status === 'suspended' || (user.status === 'cancelled' && user.linked)) {
    list.push({ label: 'Reativar', status: 'active' });
  }
  if (user.status === 'cancelled' && !user.linked) list.push({ label: 'Restaurar pré-cadastro', status: 'preapproved' });
  if (user.status !== 'cancelled') list.push({ label: 'Cancelar acesso', status: 'cancelled' });
  return list;
});

onMounted(() => {
  void loadUsers();
  void loadTenants();
});
</script>

<template>
  <section class="users">
    <h2>Usuários</h2>

    <form
      class="row"
      @submit.prevent="preRegister"
    >
      <label>
        Pré-cadastrar email
        <input
          v-model="newEmail"
          type="email"
          name="email"
          autocomplete="off"
          required
        >
      </label>
      <button
        type="submit"
        :disabled="busy"
      >
        Pré-cadastrar
      </button>
    </form>

    <form
      class="row"
      @submit.prevent="loadUsers()"
    >
      <label>
        Situação
        <select
          v-model="statusFilter"
          @change="loadUsers()"
        >
          <option value="">
            Todas
          </option>
          <option
            v-for="(label, status) in STATUS_LABEL"
            :key="status"
            :value="status"
          >
            {{ label }}
          </option>
        </select>
      </label>
      <label>
        Buscar
        <input
          v-model="search"
          type="search"
          placeholder="email ou nome"
        >
      </label>
      <button type="submit">
        Buscar
      </button>
    </form>

    <p
      v-if="message"
      role="status"
    >
      {{ message }}
    </p>

    <table>
      <thead>
        <tr>
          <th>Email</th>
          <th>Nome</th>
          <th>Situação</th>
          <th>Clientes</th>
          <th>Último acesso</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="user in users"
          :key="user.id"
          :class="{ selected: selected?.id === user.id }"
          @click="open(user.id)"
        >
          <td>{{ user.email }}</td>
          <td>{{ user.displayName ?? '—' }}</td>
          <td>{{ STATUS_LABEL[user.status] }}</td>
          <td>{{ user.tenantCount ?? 0 }}</td>
          <td>{{ lastLogin(user.lastLoginAtMs) }}</td>
        </tr>
      </tbody>
    </table>
    <button
      v-if="nextCursor !== null"
      type="button"
      @click="loadUsers(false)"
    >
      Carregar mais
    </button>

    <article
      v-if="selected"
      class="detail"
      aria-label="Acesso do usuário"
    >
      <h3>{{ selected.email }}</h3>
      <p>Situação: {{ STATUS_LABEL[selected.status] }}</p>
      <div class="row">
        <button
          v-for="action in actions"
          :key="action.status"
          type="button"
          :disabled="busy"
          @click="setStatus(action.status)"
        >
          {{ action.label }}
        </button>
      </div>

      <h4>Clientes e fazendas</h4>
      <ul class="tenants">
        <li
          v-for="tenant in tenants"
          :key="tenant.id"
        >
          <label>
            <input
              type="checkbox"
              :checked="draft.has(tenant.id)"
              @change="toggleTenant(tenant.id, ($event.target as HTMLInputElement).checked)"
            >
            {{ tenant.name }}
          </label>
          <div
            v-if="draft.has(tenant.id)"
            class="farms"
          >
            <label>
              <input
                type="checkbox"
                :checked="draft.get(tenant.id)?.allFarms"
                @change="setAllFarms(tenant.id, ($event.target as HTMLInputElement).checked)"
              >
              Todas as fazendas
            </label>
            <label
              v-for="farm in farmsByTenant[tenant.id] ?? []"
              :key="farm.id"
              :class="{ muted: draft.get(tenant.id)?.allFarms }"
            >
              <input
                type="checkbox"
                :checked="draft.get(tenant.id)?.farmIds.has(farm.id)"
                :disabled="draft.get(tenant.id)?.allFarms"
                @change="toggleFarm(tenant.id, farm.id, ($event.target as HTMLInputElement).checked)"
              >
              {{ farm.name }}
            </label>
          </div>
        </li>
      </ul>
      <button
        type="button"
        :disabled="busy"
        @click="saveGrants"
      >
        Salvar acesso
      </button>
    </article>
  </section>
</template>

<style scoped>
.users { display: grid; gap: 1rem; }
.row { display: flex; flex-wrap: wrap; gap: .75rem; align-items: end; }
label { display: inline-flex; gap: .35rem; align-items: center; }
table { border-collapse: collapse; width: 100%; }
th, td { text-align: left; padding: .35rem .5rem; border-bottom: 1px solid #ddd; }
tbody tr { cursor: pointer; }
tr.selected { background: #eef6ee; }
.detail { border: 1px solid #ddd; border-radius: 8px; padding: 1rem; display: grid; gap: .75rem; }
.tenants { list-style: none; padding: 0; display: grid; gap: .5rem; }
.farms { display: flex; flex-wrap: wrap; gap: .5rem 1rem; padding-left: 1.5rem; }
.muted { opacity: .5; }
</style>
